import * as THREE from 'three';

import {
  GHOST_DASH_FACTOR,
  GHOST_DASH_MIN,
  GHOST_GAP_FACTOR,
  GHOST_GAP_MIN,
} from './constants';
import type { ManipulatorAxis } from '../types';
import {
  angleDegreesFromLocalPoint,
  axisIndexFromKey,
  axisNormalVector,
  axisPointFromDegrees,
  normalizeDegrees,
} from '../utils';

export {
  angleDegreesFromLocalPoint,
  axisIndexFromKey,
  axisNormalVector,
  axisPointFromDegrees,
  normalizeDegrees,
};

const WHITE_COLOR = new THREE.Color(0xffffff);
const BLACK_COLOR = new THREE.Color(0x000000);

function appendArcRibbonSegment({
  positions,
  indices,
  axis,
  startDegrees,
  endDegrees,
  radius,
  halfWidth,
  segmentCount,
}: {
  positions: number[];
  indices: number[];
  axis: ManipulatorAxis;
  startDegrees: number;
  endDegrees: number;
  radius: number;
  halfWidth: number;
  segmentCount: number;
}) {
  const sweepDegrees = Math.max(0, endDegrees - startDegrees);
  if (sweepDegrees <= 1e-4) return;

  const safeSegments = Math.max(
    1,
    Math.ceil((segmentCount * sweepDegrees) / 360),
  );
  const axisNormal = axisNormalVector(axis).multiplyScalar(Math.abs(halfWidth));
  const baseVertexIndex = positions.length / 3;

  for (let i = 0; i <= safeSegments; i += 1) {
    const t = i / safeSegments;
    const angleDegrees = startDegrees + sweepDegrees * t;
    const centerPoint = axisPointFromDegrees(axis, angleDegrees, radius);
    const outerPoint = centerPoint.clone().add(axisNormal);
    const innerPoint = centerPoint.clone().sub(axisNormal);
    positions.push(
      outerPoint.x,
      outerPoint.y,
      outerPoint.z,
      innerPoint.x,
      innerPoint.y,
      innerPoint.z,
    );

    if (i === safeSegments) continue;
    const outerA = baseVertexIndex + i * 2;
    const innerA = outerA + 1;
    const outerB = outerA + 2;
    const innerB = outerA + 3;
    indices.push(outerA, innerA, outerB, innerA, innerB, outerB);
  }
}

export function createDashedAxisRingGeometry({
  axis,
  radius,
  halfWidth,
  segmentCount,
  dashDegrees,
  gapDegrees,
  angleOffsetDegrees = 0,
}: {
  axis: ManipulatorAxis;
  radius: number;
  halfWidth: number;
  segmentCount: number;
  dashDegrees: number;
  gapDegrees: number;
  angleOffsetDegrees?: number;
}): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  const safeDash = Math.max(0.1, dashDegrees);
  const safeGap = Math.max(0, gapDegrees);
  let cursor = -180;

  while (cursor < 180) {
    const dashStart = cursor;
    const dashEnd = Math.min(cursor + safeDash, 180);
    const sweepDegrees = Math.max(0, dashEnd - dashStart);
    appendArcRibbonSegment({
      positions,
      indices,
      axis,
      startDegrees: dashStart + angleOffsetDegrees,
      endDegrees: dashStart + sweepDegrees + angleOffsetDegrees,
      radius,
      halfWidth,
      segmentCount,
    });
    cursor += safeDash + safeGap;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

export function createAxisArcRibbonGeometry({
  axis,
  radius,
  halfWidth,
  segmentCount,
  startDegrees,
  endDegrees,
}: {
  axis: ManipulatorAxis;
  radius: number;
  halfWidth: number;
  segmentCount: number;
  startDegrees: number;
  endDegrees: number;
}): THREE.BufferGeometry {
  const positions: number[] = [];
  const indices: number[] = [];
  appendArcRibbonSegment({
    positions,
    indices,
    axis,
    startDegrees,
    endDegrees,
    radius,
    halfWidth,
    segmentCount,
  });

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

export function createAxisSweepFillGeometry({
  axis,
  radius,
  segmentCount,
  startDegrees,
  endDegrees,
}: {
  axis: ManipulatorAxis;
  radius: number;
  segmentCount: number;
  startDegrees: number;
  endDegrees: number;
}): THREE.BufferGeometry {
  const sweepDegrees = Math.max(0, endDegrees - startDegrees);
  const safeSegments = Math.max(
    3,
    Math.ceil((segmentCount * Math.max(sweepDegrees, 0.1)) / 360),
  );

  const positions: number[] = [0, 0, 0];
  const indices: number[] = [];
  for (let i = 0; i <= safeSegments; i += 1) {
    const t = i / safeSegments;
    const angleDegrees = startDegrees + sweepDegrees * t;
    const point = axisPointFromDegrees(axis, angleDegrees, radius);
    positions.push(point.x, point.y, point.z);
  }

  for (let i = 1; i <= safeSegments; i += 1) {
    indices.push(0, i, i + 1);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.Float32BufferAttribute(positions, 3),
  );
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

export function resolveGhostDashMetrics(subtreeExtent: number): {
  dashLength: number;
  gapLength: number;
} {
  const safeExtent = Number.isFinite(subtreeExtent) ? subtreeExtent : 0;
  return {
    dashLength: Math.max(GHOST_DASH_MIN, safeExtent * GHOST_DASH_FACTOR),
    gapLength: Math.max(GHOST_GAP_MIN, safeExtent * GHOST_GAP_FACTOR),
  };
}

export function appendAlternatingDashedSegment({
  positions,
  colors,
  start,
  end,
  dashLength,
  gapLength,
  startsWhite,
}: {
  positions: number[];
  colors: number[];
  start: THREE.Vector3;
  end: THREE.Vector3;
  dashLength: number;
  gapLength: number;
  startsWhite: boolean;
}): boolean {
  const delta = new THREE.Vector3().subVectors(end, start);
  const segmentLength = delta.length();
  if (segmentLength <= 0) return startsWhite;

  const direction = delta.multiplyScalar(1 / segmentLength);
  const safeDashLength = Math.max(1e-4, dashLength);
  const safeGapLength = Math.max(0, gapLength);
  let distance = 0;
  let useWhite = startsWhite;

  while (distance < segmentLength) {
    const dashEndDistance = Math.min(distance + safeDashLength, segmentLength);
    if (dashEndDistance > distance) {
      const dashStart = start.clone().addScaledVector(direction, distance);
      const dashEnd = start.clone().addScaledVector(direction, dashEndDistance);
      positions.push(
        dashStart.x,
        dashStart.y,
        dashStart.z,
        dashEnd.x,
        dashEnd.y,
        dashEnd.z,
      );
      const color = useWhite ? WHITE_COLOR : BLACK_COLOR;
      colors.push(
        color.r,
        color.g,
        color.b,
        color.r,
        color.g,
        color.b,
      );
      useWhite = !useWhite;
    }
    distance += safeDashLength + safeGapLength;
  }

  return useWhite;
}
